import { createMachine } from 'xstate';

import {
  CompanyProfile,
  TaxForecastProfileFamilySituation,
  TaxForecastProfileMainActivityType,
  TaxForecastProfileSubActivityType,
  TaxForecastProfileTaxCalculationMode,
} from '__generated__/GQL';

import {
  CurrentTaxForecastPeriod,
  TaxForecastProfile,
} from '../../../hooks/useTaxForecastData';
import { MicroEstimationAction, microEstimationActions } from './actions';
import { MicroEstimationGuard, microEstimationGuards } from './guards';
import { MicroEstimationState } from './state';
import { assignStepProgress, getStepTotal } from './utils';

export type MicroEstimationStepProgress = {
  total: number;
  current: number;
};

export type MicroEstimationContext = {
  isRevenueEdit: boolean;
  stepProgress: MicroEstimationStepProgress;
  taxForecastProfile?: TaxForecastProfile;
  taxForecastPeriod?: CurrentTaxForecastPeriod;
  companyProfile: Pick<
    CompanyProfile,
    'hasAccre' | 'hasPrelevLib' | 'taxFrequency' | 'companyProfileId'
  >;
};

export enum MicroEstimationEventType {
  NEXT = 'NEXT',
  BACK = 'BACK',
  FINISH = 'FINISH',
  UPDATE_REVENUE = 'UPDATE_REVENUE',
  SAVE_ACCRE_STEP = 'SAVE_ACCRE_STEP',
  SAVE_VAT_STEP = 'SAVE_VAT_STEP',
  SAVE_TAX_FREQUENCY_STEP = 'SAVE_TAX_FREQUENCY_STEP',
  SAVE_PRELEV_LIB_STEP = 'SAVE_PRELEV_LIB_STEP',
  SAVE_REVENUE_SITUATION_STEP = 'SAVE_REVENUE_SITUATION_STEP',
  SAVE_COMPANY_SITUATION_STEP = 'SAVE_COMPANY_SITUATION_STEP',
  SAVE_TAX_TYPE_STEP = 'SAVE_TAX_TYPE_STEP',
}

export type MicroEstimationtEvent =
  | { type: MicroEstimationEventType.NEXT }
  | { type: MicroEstimationEventType.BACK }
  | { type: MicroEstimationEventType.FINISH }
  | { type: MicroEstimationEventType.UPDATE_REVENUE; revenue: number }
  | {
      type: MicroEstimationEventType.SAVE_REVENUE_SITUATION_STEP;
      data: {
        bicDistributionAmount: number | null;
        bncDistributionAmount: number | null;
        complementaryRevenue: number;
        sellingDistributionAmount: number | null;
        vatAmount: number | null;
      };
    }
  | {
      type: MicroEstimationEventType.SAVE_COMPANY_SITUATION_STEP;
      data: {
        isReglementedLiberal: boolean | null;
        mainActivityType: TaxForecastProfileMainActivityType;
        subActivity: TaxForecastProfileSubActivityType | null;
      };
    }
  | {
      type: MicroEstimationEventType.SAVE_PRELEV_LIB_STEP;
      data: { hasPrelevLib: boolean };
    }
  | {
      type: MicroEstimationEventType.SAVE_ACCRE_STEP;
      data: { hasAccre: boolean };
    }
  | {
      type: MicroEstimationEventType.SAVE_VAT_STEP;
      data: { isSubjectToVat: boolean };
    }
  | {
      type: MicroEstimationEventType.SAVE_TAX_FREQUENCY_STEP;
      data: {
        taxFrequency: string;
        taxForecastPeriod: CurrentTaxForecastPeriod;
        taxForecastProfile: TaxForecastProfile;
      };
    }
  | {
      type: MicroEstimationEventType.SAVE_TAX_TYPE_STEP;
      data: {
        taxCalculationMode: TaxForecastProfileTaxCalculationMode;
        familyAdditionalRevenue?: number;
        familySituation?: TaxForecastProfileFamilySituation;
        isAloneWithChild?: boolean;
        numberOfChildren?: number;
        customTaxRate?: number;
      };
    };

const machine = createMachine<MicroEstimationContext, MicroEstimationtEvent>(
  {
    id: 'microEstimation',
    initial: MicroEstimationState.INITIAL,
    states: {
      [MicroEstimationState.INITIAL]: {
        always: [
          {
            cond: MicroEstimationGuard.ShouldOnlyDisplayRevenueSituation,
            target: MicroEstimationState.REVENUE_SITUATION,
          },
          {
            cond: (context) =>
              microEstimationGuards.shouldSkipTaxFrequency(context) &&
              microEstimationGuards.hasDoneInitialEstimation(context),
            target: MicroEstimationState.COMPANY_SITUATION,
          },
          {
            target: MicroEstimationState.TAX_FREQUENCY,
          },
        ],
      },
      [MicroEstimationState.ACCRE]: {
        entry: assignStepProgress(6),
        on: {
          BACK: MicroEstimationState.PRELEV_LIB,
          NEXT: [
            {
              cond: MicroEstimationGuard.ShouldSkipTaxType,
              target: MicroEstimationState.SUCCESS,
            },
            {
              target: MicroEstimationState.TAX_TYPE,
            },
          ],
          SAVE_ACCRE_STEP: {
            actions: [MicroEstimationAction.UpdateAccreStepContext],
          },
        },
      },
      [MicroEstimationState.COMPANY_SITUATION]: {
        entry: assignStepProgress(4),
        on: {
          BACK: [
            {
              actions: ['navigateToOverview'],
              cond: MicroEstimationGuard.HasDoneInitialEstimation,
            },
            {
              target: MicroEstimationState.VAT,
            },
          ],
          NEXT: [
            {
              cond: (context) =>
                microEstimationGuards.hasDoneInitialEstimation(context) &&
                microEstimationGuards.shouldSkipTaxType(context),
              target: MicroEstimationState.SUCCESS,
            },
            {
              cond: MicroEstimationGuard.HasDoneInitialEstimation,
              target: MicroEstimationState.TAX_TYPE,
            },
            {
              target: MicroEstimationState.PRELEV_LIB,
            },
          ],
          SAVE_COMPANY_SITUATION_STEP: {
            actions: MicroEstimationAction.UpdateCompanySituationStepContext,
          },
        },
      },
      [MicroEstimationState.PRELEV_LIB]: {
        entry: assignStepProgress(5),
        on: {
          BACK: MicroEstimationState.COMPANY_SITUATION,
          NEXT: {
            target: MicroEstimationState.ACCRE,
          },
          SAVE_PRELEV_LIB_STEP: {
            actions: [MicroEstimationAction.UpdatePrelevLibStepContext],
          },
        },
      },
      [MicroEstimationState.REVENUE_SITUATION]: {
        entry: assignStepProgress(3),
        on: {
          BACK: {
            actions: ['navigateToOverview'],
          },
          NEXT: {
            target: MicroEstimationState.SUCCESS,
          },
          SAVE_REVENUE_SITUATION_STEP: {
            actions: [MicroEstimationAction.UpdateRevenueSituationStepContext],
          },
          UPDATE_REVENUE: {
            actions: ['updateTaxPeriodRevenueContext'],
          },
        },
      },
      [MicroEstimationState.SUCCESS]: {
        entry: assignStepProgress(getStepTotal()),
        on: {
          BACK: [
            {
              cond: MicroEstimationGuard.ShouldOnlyDisplayRevenueSituation,
              target: MicroEstimationState.REVENUE_SITUATION,
            },
            {
              cond: (context) =>
                microEstimationGuards.hasDoneInitialEstimation(context) &&
                microEstimationGuards.shouldSkipTaxType(context),
              target: MicroEstimationState.COMPANY_SITUATION,
            },
            {
              cond: MicroEstimationGuard.ShouldSkipTaxType,
              target: MicroEstimationState.ACCRE,
            },
            {
              target: MicroEstimationState.TAX_TYPE,
            },
          ],
          FINISH: {
            actions: ['navigateToOverview'],
          },
        },
      },
      [MicroEstimationState.TAX_FREQUENCY]: {
        entry: assignStepProgress(1),
        on: {
          BACK: {
            actions: ['navigateToOverview'],
          },
          NEXT: {
            target: MicroEstimationState.VAT,
          },
          SAVE_TAX_FREQUENCY_STEP: {
            actions: [MicroEstimationAction.UpdateTaxFrequencyStepContext],
          },
        },
      },
      [MicroEstimationState.VAT]: {
        entry: assignStepProgress(2),
        on: {
          BACK: {
            target: MicroEstimationState.TAX_FREQUENCY,
          },
          NEXT: {
            target: MicroEstimationState.COMPANY_SITUATION,
          },
          SAVE_VAT_STEP: {
            actions: [MicroEstimationAction.UpdateVatStepContext],
          },
        },
      },
      [MicroEstimationState.TAX_TYPE]: {
        entry: assignStepProgress(7),
        on: {
          BACK: [
            {
              cond: MicroEstimationGuard.HasDoneInitialEstimation,
              target: MicroEstimationState.COMPANY_SITUATION,
            },
            {
              target: MicroEstimationState.ACCRE,
            },
          ],
          NEXT: {
            target: MicroEstimationState.SUCCESS,
          },
          SAVE_TAX_TYPE_STEP: {
            actions: [MicroEstimationAction.UpdateTaxTypeStepContext],
          },
        },
      },
    },
  },
  {
    actions: microEstimationActions,
    guards: microEstimationGuards,
  },
);

export default machine;
